import React, { FC } from 'react';
import { format, isValid } from 'date-fns';
import { Recording } from 'types/Asset';
import { CardContext, ModuleType } from 'types/Common';
import { CardTrackingData } from 'types/Tracking';
import { StandardCover } from 'types/Cover';
import useYouboraContentTracking, { ContentTrackingParams } from 'hooks/useYouboraContentTracking';
import { useBroadcastTimeCluster } from 'hooks/useBroadcastTimeCluster/useBroadcastTimeCluster';
import { getRecordingDetailURL } from 'utils/fnUrl';
import { useDetailLocationParams } from 'hooks/useDetailLocationParams';
import { LiveProgressIndicator } from 'components/LiveProgressIndicator/LiveProgressIndicator';
import { getBroadcastCardContentMarkerValue } from 'utils/fnContentMarker';
import { formatDate, isFuture } from 'utils/fnDate';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { useApp } from 'providers/useApp/AppContext';
import { useChannels } from 'providers/useChannels/useChannels';
import { getRecordingIdFromListingId } from 'utils/recordingUtils';
import { TIME_FORMAT_HH_MM } from 'utils/fnParser';
import { Card } from 'components/Cards/Card/Card';
import { CardVariant } from 'components/Cards/Card/cardVariants';

type PresenterPropTypes = {
    asset: Recording;
    type: ModuleType;
    cardContext: CardContext;
    trackingData: CardTrackingData;
    onMouseEnter?: (event: React.MouseEvent) => void;
    onMouseLeave?: (event: React.MouseEvent) => void;
    onClick?: (event: React.MouseEvent) => void;
};

export const RecordingCardPresenter: FC<PresenterPropTypes> = ({ asset, type, cardContext, trackingData }) => {
    const { id, title, image, image_url, isPortrait, start, duration, isLive, bookmark, channelId } = asset;
    const recordingId = getRecordingIdFromListingId(asset.id) || asset.recordingId;
    const timeCluster = useBroadcastTimeCluster(asset);
    const locationParamsPath = useDetailLocationParams();
    const { config } = useConfig();
    const { appLanguage } = useApp();
    const { getChannelById } = useChannels();
    const channelLogo = getChannelById(channelId)?.logo;
    const endTime = start + duration;

    const { handleContentFocus, handleContentFocusOut, handleContentClick } = useYouboraContentTracking();

    const path = getRecordingDetailURL(asset, locationParamsPath, appLanguage);

    const startDate = new Date(start * 1000);
    const endDate = new Date(endTime * 1000);
    const dateString = formatDate(startDate);

    const metaData =
        isValid(startDate) && isValid(endDate)
            ? `${dateString}, ${format(startDate, TIME_FORMAT_HH_MM)} - ${format(endDate, TIME_FORMAT_HH_MM)}`
            : '';

    const progress = (bookmark * 100) / duration;

    const youboraParams: ContentTrackingParams = {
        section: trackingData['data-module-title'] || '',
        sectionOrder: trackingData['data-stripe-position'] || 1,
        column: trackingData['data-card-position'] || 1,
        row: 1,
        contentId: asset.id,
        contentLink: asset.id,
        contentLinkType: 'contentId',
    };

    const contentTrackingProps = {
        onMouseEnter: () => handleContentFocus(youboraParams),
        onMouseLeave: handleContentFocusOut,
        onClick: () => handleContentClick(youboraParams),
    };

    const cover: StandardCover = {
        uuid: id + title,
        id,
        title,
        image: image_url,
        landscapeImage: image,
        path,
        metaData,
        circle: false,
        landscape: !isPortrait,
        assetType: asset.type,
        rawData: { ...asset, recordingId },
        channelImage: channelLogo,
        contentMarker: getBroadcastCardContentMarkerValue(isLive, timeCluster),
        progressBar:
            timeCluster === 'LIVE' ? (
                <LiveProgressIndicator startTime={start} endTime={endTime} onLiveStateChanged={null} />
            ) : (
                !isFuture(start) && (
                    <ProgressIndicator percentage={progress < config.app_config.player_settings.bookmark_threshold ? progress : 0} />
                )
            ),
    };

    const cardProps = {
        asset: cover,
        ...trackingData,
        ...contentTrackingProps,
    };

    const variant = () => {
        if (type === ModuleType.ROUNDED) {
            return CardVariant.CIRCLE;
        }

        if (type === ModuleType.VOD_MOVIE) {
            return CardVariant.PORTRAIT;
        }

        if (cardContext !== CardContext.STRIPE) {
            return CardVariant.LANDSCAPE;
        }

        return type !== ModuleType.MIXED && isPortrait ? CardVariant.PORTRAIT : CardVariant.LANDSCAPE;
    };

    return <Card variant={variant()} context={cardContext} {...cardProps} />;
};
